import React, { useState, useEffect, useRef } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import useMunicipalitySettings, { useUpdateMunicipalitySettings } from 'api/hooks/use-municipality-settings'
import { useQueryClient } from 'react-query'

function BrandingSettings() {
  const queryClient = useQueryClient()
  const { data: settings, isLoading } = useMunicipalitySettings({ options: {} })
  const fileInputRef = useRef(null)

  const [formData, setFormData] = useState({
    color_1: '',
    color_2: '',
    color_3: '',
    color_4: '',
    color_5: '',
    logo: null
  })
  const [logoPreview, setLogoPreview] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    if (settings) {
      setFormData({
        color_1: settings.color_1 || '',
        color_2: settings.color_2 || '',
        color_3: settings.color_3 || '',
        color_4: settings.color_4 || '',
        color_5: settings.color_5 || '',
        logo: null
      })

      if (settings.logo_url) {
        setLogoPreview(settings.logo_url)
      }
    }
  }, [settings])

  const updateMutation = useUpdateMunicipalitySettings({
    hasFile: formData.logo !== null,
    options: {
      onSuccess: (data) => {
        setSaveSuccess(true)
        setSaveError(null)
        if (data?.settings) {
          queryClient.setQueryData('municipality-settings', data.settings)
        } else {
          queryClient.invalidateQueries('municipality-settings')
        }
        setTimeout(() => setSaveSuccess(false), 3000)
      },
      onError: (error) => {
        setSaveError(error.response?.data?.message || 'Failed to save settings')
        setSaveSuccess(false)
      }
    }
  })

  const handleColorChange = (colorNumber, value) => {
    setFormData({
      ...formData,
      [`color_${colorNumber}`]: value
    })
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData({
        ...formData,
        logo: file
      })

      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveLogo = () => {
    setFormData({
      ...formData,
      logo: null
    })
    setLogoPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    updateMutation.mutate(formData)
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <Row>
      <Col>
        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col>
                  <div className="text-primary fw-bold mb-2">Branding</div>
                </Col>
              </Row>

              {saveSuccess && (
                <Alert variant="success" className="mt-3">
                  Settings saved successfully!
                </Alert>
              )}

              {saveError && (
                <Alert variant="danger" className="mt-3">
                  {saveError}
                </Alert>
              )}

              <Row className="mt-4">
                <Col>
                  <div className="text-primary fw-bold mb-2">
                    Logo
                    <hr />
                  </div>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={8}>
                  <p className="mt-1">Municipality Logo</p>
                  <small className="text-muted">Upload a logo for your municipality (PNG, JPG, or SVG)</small>
                </Col>
                <Col sm={4}>
                  {logoPreview && (
                    <div className="mb-2">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        style={{ maxWidth: '200px', maxHeight: '100px' }}
                        className="img-thumbnail"
                      />
                      <div className="mt-2">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRemoveLogo}
                        >
                          Remove Logo
                        </Button>
                      </div>
                    </div>
                  )}
                  <Form.Group>
                    <Form.Control
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col>
                  <div className="text-primary fw-bold mb-2">
                    Brand Colors
                    <hr />
                  </div>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={8}>
                  <p className="mt-1">Color 1</p>
                  <small className="text-muted">Primary brand color</small>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        value={formData.color_1 || '#000000'}
                        onChange={(e) => handleColorChange(1, e.target.value)}
                        style={{ width: '60px', marginRight: '10px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.color_1}
                        onChange={(e) => handleColorChange(1, e.target.value)}
                        placeholder="#000000"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={8}>
                  <p className="mt-1">Color 2</p>
                  <small className="text-muted">Secondary brand color</small>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        value={formData.color_2 || '#000000'}
                        onChange={(e) => handleColorChange(2, e.target.value)}
                        style={{ width: '60px', marginRight: '10px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.color_2}
                        onChange={(e) => handleColorChange(2, e.target.value)}
                        placeholder="#000000"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={8}>
                  <p className="mt-1">Color 3</p>
                  <small className="text-muted">Additional brand color</small>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        value={formData.color_3 || '#000000'}
                        onChange={(e) => handleColorChange(3, e.target.value)}
                        style={{ width: '60px', marginRight: '10px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.color_3}
                        onChange={(e) => handleColorChange(3, e.target.value)}
                        placeholder="#000000"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={8}>
                  <p className="mt-1">Color 4</p>
                  <small className="text-muted">Additional brand color</small>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        value={formData.color_4 || '#000000'}
                        onChange={(e) => handleColorChange(4, e.target.value)}
                        style={{ width: '60px', marginRight: '10px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.color_4}
                        onChange={(e) => handleColorChange(4, e.target.value)}
                        placeholder="#000000"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={8}>
                  <p className="mt-1">Color 5</p>
                  <small className="text-muted">Additional brand color</small>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        value={formData.color_5 || '#000000'}
                        onChange={(e) => handleColorChange(5, e.target.value)}
                        style={{ width: '60px', marginRight: '10px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.color_5}
                        onChange={(e) => handleColorChange(5, e.target.value)}
                        placeholder="#000000"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={updateMutation.isLoading}
                  >
                    {updateMutation.isLoading ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default BrandingSettings
